/**
 * Client-side cookie utilities
 */

export function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return undefined;
}

export function setCookie(name: string, value: string, options: {
  path?: string;
  maxAge?: number;
  expires?: Date;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
} = {}) {
  let cookie = `${name}=${value}`;
  
  if (options.path) cookie += `; path=${options.path}`;
  else cookie += '; path=/';
  
  if (options.maxAge) cookie += `; max-age=${options.maxAge}`;
  else if (options.expires) cookie += `; expires=${options.expires.toUTCString()}`;
  else cookie += `; max-age=${60 * 60 * 24 * 7}`; // default: 7 days
  
  if (options.secure) cookie += '; secure';
  if (options.sameSite) cookie += `; samesite=${options.sameSite}`;
  
  document.cookie = cookie;
}

export function deleteCookie(name: string, path = '/') {
  document.cookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

// Function overload: when fallback is provided, always returns T
export function getPreference<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T
): T;

// Function overload: when no fallback, returns T | undefined
export function getPreference<T extends string>(
  key: string,
  allowed: readonly T[]
): T | undefined;

// Implementation
export function getPreference<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback?: T
): T | undefined {
  const value = getCookie(key)?.trim();
  if (allowed.includes(value as T)) {
    return value as T;
  }
  return fallback;
}

export function setPreference(key: string, value: string): void {
  setCookie(key, value);
}
